import IMask from 'imask';

import { PhoneCountryConfig } from '@/shared/types/phone';

export function formatTelefone(value: string): string {
  const cleaned = value.replace(/\D/g, '');

  if (cleaned.length <= 10) {
    return cleaned.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  }

  return cleaned
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
}

export const formatPhone = (value: string): string => {
  let digits = value.replace(/\D/g, '');

  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    digits = digits.slice(2);
  }

  digits = digits.slice(0, 11);

  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export function removeTelefoneMask(value: string): string {
  return value.replace(/\D/g, '');
}

const emailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export function isValidEmail(value: string): boolean {
  return emailRegex.test(value.trim().toLowerCase());
}

export function isValidTelefone(value: string): boolean {
  const digits = removeTelefoneMask(value);
  return digits.length >= 10 && digits.length <= 13;
}

export function isCanalTelefone(canal: string): boolean {
  return /whatsapp|telefone|phone/i.test(canal);
}

export function formatTelefoneComCodigoPais(value: string): string {
  const digits = removeTelefoneMask(value);
  if (digits.length === 0) return value;

  // O 55 também é DDD (interior do RS): só é código do país quando sobra número local depois dele.
  const comCodigoDoPais = digits.startsWith('55') && digits.length > 11;
  const localDigits = comCodigoDoPais ? digits.slice(2) : digits;

  if (localDigits.length === 10 || localDigits.length === 11) {
    const mask = localDigits.length === 11 ? '+{55} (00) 00000-0000' : '+{55} (00) 0000-0000';
    const phoneMask = IMask.createMask({ mask });
    // Com o 55 na frente, é ele que o `+{55}` fixo da máscara consome — e não o DDD 55 do número.
    phoneMask.resolve(`55${localDigits}`);
    return phoneMask.value;
  }

  return value;
}

function applyUsPhoneMask(value: string): string {
  const digits = value.replace(/\D/g, '');
  return digits
    .replace(/(\d{3})(\d)/, '($1) $2')
    .replace(/(\d{3})(\d)/, '$1-$2')
    .slice(0, 14);
}

function applyGenericPhoneMask(value: string, maxDigits: number = 15): string {
  return value.replace(/\D/g, '').slice(0, maxDigits);
}

function applyMexicoPhoneMask(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return digits.replace(/(\d{2})(\d)/, '$1 $2');
  return digits.replace(/(\d{2})(\d{4})(\d)/, '$1 $2 $3').slice(0, 12);
}

export const phoneCountryConfigs: PhoneCountryConfig[] = [
  {
    code: '+55',
    label: 'Brasil',
    flag: '🇧🇷',
    mask: formatPhone,
    placeholder: '(00) 00000-0000',
  },
  {
    code: '+1',
    label: 'EUA/Canadá',
    flag: '🇺🇸',
    mask: applyUsPhoneMask,
    placeholder: '(000) 000-0000',
  },
  {
    code: '+351',
    label: 'Portugal',
    flag: '🇵🇹',
    mask: applyGenericPhoneMask,
    placeholder: '000 000 000',
  },
  {
    code: '+44',
    label: 'Reino Unido',
    flag: '🇬🇧',
    mask: applyGenericPhoneMask,
    placeholder: '0000 000000',
  },
  {
    code: '+34',
    label: 'Espanha',
    flag: '🇪🇸',
    mask: applyGenericPhoneMask,
    placeholder: '000 00 00 00',
  },
  {
    code: '+33',
    label: 'França',
    flag: '🇫🇷',
    mask: applyGenericPhoneMask,
    placeholder: '0 00 00 00 00',
  },
  {
    code: '+49',
    label: 'Alemanha',
    flag: '🇩🇪',
    mask: applyGenericPhoneMask,
    placeholder: '000 0000000',
  },
  {
    code: '+39',
    label: 'Itália',
    flag: '🇮🇹',
    mask: applyGenericPhoneMask,
    placeholder: '000 000 0000',
  },
  {
    code: '+54',
    label: 'Argentina',
    flag: '🇦🇷',
    mask: applyGenericPhoneMask,
    placeholder: '00 0000-0000',
  },
  {
    code: '+56',
    label: 'Chile',
    flag: '🇨🇱',
    mask: applyGenericPhoneMask,
    placeholder: '0 0000 0000',
  },
  {
    code: '+52',
    label: 'México',
    flag: '🇲🇽',
    mask: applyMexicoPhoneMask,
    placeholder: '00 0000 0000',
  },
];
